import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, LogIn, Key, Eye, EyeOff, Fingerprint, Mail, ArrowLeft } from "lucide-react";
import { PasskeyLogin } from "@/components/auth/PasskeyLogin";

type AuthStep = "login" | "magic-link" | "password-reset" | "passkey-login";

const AuthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");

  const [authStep, setAuthStep] = useState<AuthStep>("login");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Helper function to determine where to redirect after login
  const getRedirectPath = async (userId: string): Promise<string> => {
    try {
      const { data: roleData } = await supabase
        .rpc("get_primary_role", { p_user_id: userId });

      const role = roleData as string | null;

      if (role === "employee" || role === "hr_clerk") {
        if (role === "hr_clerk") {
          return "/employees";
        }
        const { data: profileData } = await supabase
          .from("profiles")
          .select("employee_id")
          .eq("id", userId)
          .single();

        if (profileData?.employee_id) {
          return `/employee/${profileData.employee_id}`;
        }
        return "/directory";
      }

      return "/";
    } catch (error) {
      console.error("Error determining redirect path:", error);
      return "/";
    }
  };

  const completeLogin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      toast.success("Erfolgreich angemeldet!");
      const redirectPath = await getRedirectPath(user.id);
      navigate(redirectPath);
    }
  };

  useEffect(() => {
    const isRecoveryFlow =
      window.location.hash.includes("type=recovery") ||
      window.location.search.includes("type=recovery");

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && !isRecoveryFlow) {
        const redirectPath = await getRedirectPath(session.user.id);
        navigate(redirectPath);
      }
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth event:", event);

      if (event === "PASSWORD_RECOVERY") {
        setAuthStep("password-reset");
        return;
      }

      if (event === "SIGNED_IN" && session && !isRecoveryFlow) {
        const redirectPath = await getRedirectPath(session.user.id);
        navigate(redirectPath);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) throw error;

      if (data.user?.user_metadata?.must_change_password) {
        setAuthStep("password-reset");
        toast.info("Bitte ändern Sie Ihr temporäres Passwort.");
        setLoading(false);
        return;
      }

      await completeLogin();
    } catch (error: any) {
      if (error.message === "Invalid login credentials") {
        toast.error("Ungültige E-Mail oder Passwort");
      } else {
        toast.error(error.message || "Fehler beim Anmelden");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Das Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }

    setLoading(true);

    try {
      const { error: passwordError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (passwordError) throw passwordError;

      const { error: metadataError } = await supabase.auth.updateUser({
        data: { must_change_password: false }
      });

      if (metadataError) {
        console.error("Error updating metadata:", metadataError);
      }

      toast.success("Passwort erfolgreich geändert!");
      setNewPassword("");
      setConfirmPassword("");

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const redirectPath = await getRedirectPath(user.id);
        navigate(redirectPath);
      }
    } catch (error: any) {
      console.error("Password update error:", error);
      toast.error(error.message || "Fehler beim Ändern des Passworts");
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLoginSuccess = async () => {
    await completeLogin();
  };

  // Passkey Login Step
  if (authStep === "passkey-login") {
    return (
      <PasskeyLogin
        onSuccess={handlePasskeyLoginSuccess}
        onBack={() => setAuthStep("login")}
        onNoPasskey={() => {
          toast.info("Kein Passkey gefunden. Bitte melden Sie sich mit Passwort an.");
          setAuthStep("login");
        }}
      />
    );
  }

  // Password Reset Form
  if (authStep === "password-reset") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl flex items-center justify-center gap-2">
              <Key className="h-6 w-6" />
              Neues Passwort setzen
            </CardTitle>
            <CardDescription>
              Bitte geben Sie Ihr neues Passwort ein
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mindestens 6 Zeichen"
                    required
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Passwort wiederholen"
                    required
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gespeichert...
                  </>
                ) : (
                  <>
                    <Key className="mr-2 h-4 w-4" />
                    Passwort ändern
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Magic Link Step
  if (authStep === "magic-link") {
    const handleMagicLink = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email: magicLinkEmail,
          options: {
            emailRedirectTo: window.location.origin + "/auth",
          },
        });
        if (error) throw error;
        setMagicLinkSent(true);
        toast.success("Magic Link wurde gesendet! Prüfen Sie Ihr Postfach.");
      } catch (error: any) {
        toast.error(error.message || "Fehler beim Senden des Magic Links");
      } finally {
        setLoading(false);
      }
    };

    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl flex items-center justify-center gap-2">
              <Mail className="h-6 w-6" />
              Magic Link Login
            </CardTitle>
            <CardDescription>
              Erhalten Sie einen Anmeldelink per E-Mail – kein Passwort nötig
            </CardDescription>
          </CardHeader>
          <CardContent>
            {magicLinkSent ? (
              <div className="text-center space-y-4">
                <div className="bg-muted border border-border rounded-lg p-4">
                  <Mail className="h-10 w-10 text-primary mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground">
                    Ein Anmeldelink wurde an <strong>{magicLinkEmail}</strong> gesendet.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Klicken Sie auf den Link in der E-Mail, um sich anzumelden.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setMagicLinkEmail("");
                    setAuthStep("login");
                  }}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück zur Anmeldung
                </Button>
              </div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="magic-email">E-Mail</Label>
                  <Input
                    id="magic-email"
                    type="email"
                    value={magicLinkEmail}
                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Magic Link senden
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setAuthStep("login")}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück zur Passwort-Anmeldung
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    );
  }

  // Login Form - Authentication Page
  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Flash HR Personalplattform</CardTitle>
          <CardDescription>
            Melden Sie sich mit Ihren Zugangsdaten an
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">E-Mail</Label>
              <Input
                id="login-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="ihre@email.de"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Passwort</Label>
              <Input
                id="login-password"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird angemeldet...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Anmelden
                </>
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">oder</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setAuthStep("passkey-login")}
            >
              <Fingerprint className="mr-2 h-4 w-4" />
              Mit Passkey anmelden
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setAuthStep("magic-link")}
            >
              <Mail className="mr-2 h-4 w-4" />
              Mit Magic Link anmelden
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-6">
            Noch keinen Zugang? Wenden Sie sich an Ihren Administrator.
          </p>
        </CardContent>
      </Card>
    </main>
  );
};

export default AuthPage;
